/**
 * What the `/downloads` card needs to know about a Frame picker result — FRAME_PICKER_DESIGN.md
 * §4.1 (the processing note), §6.1 ("Pick another frame" → `?tab=picker&psid=<sid>`), §6.5 (the
 * card: time badge, ZIP primary for sequences).
 *
 * THE NOTE. Ironman writes ONE tagged line into `processResult.processingNotes` (shared-lib
 * `FramePickerNaming.toProcessingNote()`, P2.6): `framePicker mode=ONE_FRAME t=1.567` (R14-SRV5: `… t=1.416 frame=44
 * at=1.433` on a CFR source), `framePicker mode=SEQUENCE`, `framePicker mode=SHEET`. Tokens are `key=value`, whitespace
 * separated, unknown tokens tolerated — the same grammar the Kotlin parser reads. The card
 * used to render every note verbatim ("ⓘ framePicker mode=…"); this module is how it tells the
 * machine line from the human ones.
 *
 * THE SID. The picker records `sid → workflowId` in its `FRAMEPICKER-<sid>` localStorage record
 * before it navigates to `/downloads` (§6.1 `onConverted$`, §6.4). The card holds only the
 * `workflowId` (`FileUpload.workflowId`, since 32dd0889), so it asks the picker's own storage
 * layer (`components/frame-picker/state/persistence.ts#findRecordByWorkflow`) which record
 * names it. READ-ONLY, with ONE exception: the card adapts the `StorageService` it already holds to
 * persistence's `PickerStorage` and never writes through it, except to hand the card's terminal
 * verdict to persistence's own `recordJobFinish` (R14-SH16 ← R14Q-56: the picker's duplicate guard
 * holds until the job is marked done / failed, and only `/downloads` learns which). The record's
 * shape is persistence's to define and parse — this module no longer guesses at it.
 *
 * `formatHms` is `components/frame-picker/media/timeline.ts#formatHms`, re-exported so the
 * badge and the picker's timecode can never drift apart.
 *
 * No React, no DI, no clock: a pure module so the card's spec needs neither.
 */
import { COPY as FRAME_PICKER_COPY } from '../../frame-picker/copy';
import { TYPED_TIME_SLACK_S, Timeline, createTimeline, formatHms, frameFromWire, frameIndex, frameStart } from '../../frame-picker/media/timeline';
import { findRecordByWorkflow, FRAMEPICKER_PREFIX, fromRecord, parseRecord, PICKED_FRAMES_OPTION, PickerRecord, PickerStorage, recordJobFinish, sidFromKey, StagedFileRef, thumbFor, userFrameTimeOf } from '../../frame-picker/state/persistence';
import { frameCount as pickerFrameCount, modeFromOption, PickerState } from '../../frame-picker/state/picker-state';
import { DOWNLOADS_LINK_SOURCE, isValidPickerSid, isValidPickerWorkflowId, PICKER_DOMAIN_PARAM, PICKER_FRESH_PARAM, PICKER_JOB_PARAM, PICKER_PSRC_PARAM, PICKER_TAB_ID, PICKER_WORKFLOW_PARAM, pickerProcessDomainOf } from '../../frame-picker/state/picker-url';
import { X2_COPY } from '../../../utils/upload-expiry';

export { formatHms };

/** Editor re-open T5 (R-D): `PICKED` = the pinned frames as ONE job (`SCREENSHOT_PICKED_FRAMES`) -- one card, N files, the ZIP. */
export type FramePickerMode = 'ONE' | 'SEQUENCE' | 'SHEET' | 'PICKED';

/** T5: one pinned frame of a PICKED job -- `frame` (1-based, CFR only) and its time `at` in seconds. */
export interface PickedFrame {
    frame?: number;
    at: number;
}

export interface FramePickerNote {
    mode: FramePickerMode;
    /** Source time of the frame in seconds; `null` unless `mode === 'ONE'` and the line carried `t=`. */
    t: number | null;
    /**
     * IT1 (W4 be #8, ironman ≥ 40acbaf8): a SHEET line carries `labelsDropped=true|false` -- true when the edge's ffmpeg
     * could not draw the time stamps and the title (no `drawtext`), so the delivered sheet has neither. `null` on an
     * older jar's line, on other modes, and on the request-side stand-in.
     */
    labelsDropped: boolean | null;
    /**
     * R14 (R14Q-14, R14-SRV5 ironman ≥ b8940ab9): a ONE_FRAME line on a CFR source carries the frame the server DELIVERED --
     * `frame=<n>` (1-based, the `f<n>` of the saved name) and `at=<s>` (that frame's start, the `<clock(at)>` of the name).
     * Present only when the line carries them (a VFR / unknown-rate source and an older jar write neither), so a card can tell
     * "the server said 44" from "nobody said".
     */
    frame?: number;
    at?: number;
    /**
     * R14 (R14U-18, R14-SRV7 ironman ≥ c8653a59): a SEQUENCE / SHEET line names the job's range and count -- `start=<s>`
     * `end=<s>` (on a CFR source the frames the user set, `start=2 end=4`; raw seconds under a variable rate) and `frames=<n>`
     * (the frames asked of the edge). Present only when the line carries them (an older jar writes the mode alone); the
     * request-side stand-in carries `frames` off `screenshotFrameCount` and never the wire's quarter-frame-early start.
     */
    start?: number;
    end?: number;
    frames?: number;
    /**
     * Editor re-open T5 (ironman 6f6fa9b9): a PICKED line lists its frames -- `picks=44@1.433,47@1.533` on a CFR source (1-based frame @
     * its own time), `picks=@1.45,@2` otherwise -- in time order; the request stand-in carries the wire's times only.
     */
    picks?: PickedFrame[];
}

/** IT1 (be #8): the one line the card says when a sheet came without its labels (the picker's copy table owns the words). */
export const FRAME_PICKER_LABELS_DROPPED_COPY = FRAME_PICKER_COPY.sheetLabelsDropped;

/** First token of the note line (shared-lib `FramePickerNaming.NOTE_PREFIX`). */
export const FRAME_PICKER_NOTE_PREFIX = 'framePicker';

/** The `StorageService` prefix half of `FRAMEPICKER-<sid>` (§6.4) — persistence's, re-exported. */
export const FRAMEPICKER_STORAGE_PREFIX = FRAMEPICKER_PREFIX;

/**
 * The picker's own URL vocabulary (§6.1, §6.4) comes from the picker (`state/picker-url.ts`):
 * the tab value and the sid shape are IMPORTED, never spelled here, so the card can only ever
 * write a URL `readPickerUrl` reads back. `PICKER_TAB_ID` is re-exported for the card's callers.
 *
 * `psid` is the one spelling still local: `picker-url.ts` writes the key as an object literal
 * (`pushQuery({ tab, psid })`) and exports no name for it. The spec pins the round trip through
 * `readPickerUrl`, so a rename there fails here rather than silently splitting the two.
 */
export { PICKER_TAB_ID };
export const PICKER_SID_PARAM = 'psid';

/**
 * Both spellings of the single-frame mode are accepted: the design's tables say `ONE`
 * (§4.1 `{mode, t | …}`), the shared-lib enum that actually writes the line says `ONE_FRAME`
 * (`FramePickerNaming.Mode`). Recorded as design anchor drift in the W6 report; reading both
 * costs nothing and means a card never mis-renders over a spelling.
 */
const MODES: Record<string, FramePickerMode> = {
    ONE: 'ONE',
    ONE_FRAME: 'ONE',
    SEQUENCE: 'SEQUENCE',
    SHEET: 'SHEET',
    // R14 (R14U-18): the request's own spelling (`videoToImageFrameSelection=CONTACT_SHEET`), should a jar ever echo it.
    CONTACT_SHEET: 'SHEET',
    // Editor re-open T5: the edge writes `mode=PICKED`; the request's spelling too.
    PICKED: 'PICKED',
    [PICKED_FRAMES_OPTION]: 'PICKED',
};

/** T5: `44@1.433,47@1.533` / `@1.45,@2` → the frames, garbage entries dropped one by one, in time order. */
export function parsePicks(raw: unknown): PickedFrame[] {
    if (typeof raw !== 'string' || raw === '') return [];
    const out: PickedFrame[] = [];
    for (const entry of raw.split(',')) {
        const m = /^(\d*)@([0-9]*\.?[0-9]+)$/.exec(entry.trim());
        if (!m) continue;
        const at = Number(m[2]);
        if (!Number.isFinite(at) || at < 0) continue;
        const pick: PickedFrame = { at };
        const frame = m[1] === '' ? NaN : Number(m[1]);
        if (Number.isInteger(frame) && frame >= 1) pick.frame = frame;
        out.push(pick);
    }
    return out.sort((a, b) => a.at - b.at);
}

/** Parse one note line; `null` unless it is a `framePicker` line naming a known mode. */
export function parseFramePickerNote(note: unknown): FramePickerNote | null {
    if (typeof note !== 'string') return null;
    const tokens = note.trim().split(/\s+/);
    if (tokens.length === 0 || tokens[0] !== FRAME_PICKER_NOTE_PREFIX) return null;
    const fields: Record<string, string> = {};
    for (const token of tokens.slice(1)) {
        const eq = token.indexOf('=');
        if (eq <= 0) continue;
        fields[token.slice(0, eq)] = token.slice(eq + 1);
    }
    const mode = MODES[fields.mode];
    if (!mode) return null;
    let t: number | null = null;
    if (mode === 'ONE' && fields.t !== undefined) {
        const parsed = Number(fields.t);
        t = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    }
    const labelsDropped = mode === 'SHEET' && (fields.labelsDropped === 'true' || fields.labelsDropped === 'false') ? fields.labelsDropped === 'true' : null;
    const out: FramePickerNote = { mode, t, labelsDropped };
    if (mode === 'ONE') {
        const frame = /^\d+$/.test(fields.frame ?? '') ? Number(fields.frame) : NaN;
        const at = Number(fields.at);
        if (Number.isInteger(frame) && frame >= 1) out.frame = frame;
        if (fields.at !== undefined && Number.isFinite(at) && at >= 0) out.at = at;
    } else if (mode === 'PICKED') {
        const picks = parsePicks(fields.picks);
        if (picks.length) out.picks = picks;
        const frames = /^\d+$/.test(fields.frames ?? '') ? Number(fields.frames) : NaN;
        if (Number.isInteger(frames) && frames >= 1) out.frames = frames;
        else if (picks.length) out.frames = picks.length;
    } else {
        // R14U-18: the range and the count, each dropped alone when garbage; `end` must not precede `start`.
        const start = Number(fields.start);
        const end = Number(fields.end);
        const frames = /^\d+$/.test(fields.frames ?? '') ? Number(fields.frames) : NaN;
        if (fields.start !== undefined && Number.isFinite(start) && start >= 0) out.start = start;
        if (fields.end !== undefined && Number.isFinite(end) && end >= 0 && (out.start === undefined || end >= out.start)) out.end = end;
        if (Number.isInteger(frames) && frames >= 1) out.frames = frames;
    }
    return out;
}

/**
 * Is this note the machine line? A `framePicker` line with an UNKNOWN mode is still the machine
 * line — it is filtered from the human rendering either way, so a future mode never shows up
 * as "ⓘ framePicker mode=…" on the card.
 */
export function isFramePickerNoteLine(note: unknown): boolean {
    return typeof note === 'string' && note.trim().split(/\s+/)[0] === FRAME_PICKER_NOTE_PREFIX;
}

/** The picker context carried by a result's notes, or `null` for an ordinary conversion. */
export function framePickerNoteOf(notes: unknown): FramePickerNote | null {
    if (!Array.isArray(notes)) return null;
    for (const note of notes) {
        const parsed = parseFramePickerNote(note);
        if (parsed) return parsed;
    }
    return null;
}

/** The notes a human should read: everything but the machine line(s). */
export function withoutFramePickerNotes(notes: unknown): string[] {
    if (!Array.isArray(notes)) return [];
    return notes.filter(note => !isFramePickerNoteLine(note));
}

/**
 * The picker context read from the REQUEST while the card is still queued (picker-fix Q16): the
 * processing note only exists once ironman has finished, so a picker card in flight would show
 * no badge. The request carries the same facts — `videoToImageFrameSelection` names the mode and
 * `screenshotTime` (`"1.567::SECONDS"`, `export/convert.ts#writeOptions`) the frame — but Quick
 * convert writes the very same keys, so the options alone do not say "picker". `fromPicker` is
 * that discriminator: the card asks `pickerSidForWorkflow` (the picker recorded this workflow in
 * its `FRAMEPICKER-<sid>` record) and passes the verdict in. `null` when not from the picker or
 * when the options name no known mode. Pure; the card composes it with the note.
 */
export function framePickerNoteFromRequest(options: unknown, fromPicker: boolean): FramePickerNote | null {
    if (!fromPicker || !options || typeof options !== 'object') return null;
    const o = options as Record<string, unknown>;
    // Editor re-open T5: the fourth selection is not a picker tree mode (`modeFromOption` knows three) -- the pins' seek instants
    // ride in ONE `screenshotTimes` string (`"1.433::SECONDS,1.533::SECONDS"`).
    if (o.videoToImageFrameSelection === PICKED_FRAMES_OPTION) {
        const raw = typeof o.screenshotTimes === 'string' ? o.screenshotTimes : Array.isArray(o.screenshotTimes) ? o.screenshotTimes.join(',') : '';
        const picks = parsePicks(raw.split(',').map((e) => `@${String(e).split('::')[0].trim()}`).join(','));
        const seen = new Set<number>();
        const distinct = picks.filter((p) => (seen.has(p.at) ? false : (seen.add(p.at), true)));
        const out: FramePickerNote = { mode: 'PICKED', t: null, labelsDropped: null };
        if (distinct.length) { out.picks = distinct; out.frames = distinct.length; }
        return out;
    }
    const mode = modeFromOption(o.videoToImageFrameSelection);
    if (!mode) return null;
    let t: number | null = null;
    if (mode === 'ONE') {
        const raw = typeof o.screenshotTime === 'string' ? o.screenshotTime.split('::')[0] : o.screenshotTime;
        const parsed = Number(raw);
        t = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
        return { mode, t, labelsDropped: null };
    }
    // R14U-18: a BY_COUNT sequence and a sheet carry their count on the wire (`screenshotFrameCount`, `picker-state.ts#toOptionWrites`);
    // an interval-spaced sequence writes a rate instead and says nothing here. The wire's start is a quarter frame early
    // (`frameSeekTime`), so the range is NOT read off the request -- the record and the landed note own it.
    const count = Number(typeof o.screenshotFrameCount === 'string' ? o.screenshotFrameCount.split('::')[0] : o.screenshotFrameCount);
    const out: FramePickerNote = { mode, t, labelsDropped: null };
    if (Number.isInteger(count) && count >= 1) out.frames = count;
    return out;
}

/**
 * The two `StorageService` methods this module reads, and the one it writes through for the verdict
 * (`recordPickerJobFinish`; optional -- a store without it is read-only and the verdict is not recorded). Structural, so a
 * spec needs no DI.
 */
export interface PickerRecordStore {
    keySet(keyPrefix?: string): Set<string> | undefined;
    get<T>(prefix: string, key: string): T;
    store?(prefix: string, key: string, value: unknown): unknown;
}

/**
 * `StorageService` seen as persistence's `PickerStorage`, READ-ONLY: `write` reports failure
 * and `remove` does nothing, so no path through this module can touch a picker record (R9).
 * `keySet` returns FULL keys (`FRAMEPICKER-<sid>`, and the `FRAMEPICKER-LATEST-<slug>`
 * bookkeeping keys), which is exactly what `PickerStorage.keys()` promises; persistence's own
 * `sidFromKey` filters the LATEST ones. A throwing store reads as empty, never as an exception.
 */
function readOnlyStorage(store: PickerRecordStore): PickerStorage {
    const head = `${FRAMEPICKER_STORAGE_PREFIX}-`;
    return {
        read(fullKey) {
            if (!fullKey.startsWith(head)) return null;
            try {
                return store.get<unknown>(FRAMEPICKER_STORAGE_PREFIX, fullKey.slice(head.length)) ?? null;
            } catch {
                return null;
            }
        },
        write() {
            return false;
        },
        remove() { /* read-only */ },
        keys() {
            try {
                return Array.from(store.keySet(head) ?? []);
            } catch {
                return [];
            }
        },
    };
}

/**
 * The picker session (`sid`) that produced `workflowId`, or `null` when no record names it —
 * storage disabled (D5), the record swept after 8 h (§6.4), or a conversion that did not come
 * from the picker at all. Never throws.
 */
/**
 * IT2 (Y2, qa N1): the time the USER picked for this workflow, or null. The request carries the ffmpeg
 * SEEK instant (`-ss` takes the first frame with pts >= t, so a typed 1.567 is asked as 1.55); the
 * picker's record keeps the user's time under that seek (`frameTimes`), and the card's badge must say
 * what the user picked, not what ffmpeg was asked.
 */
export function pickerFrameTimeForWorkflow(workflowId: unknown, seek: number | string | null | undefined, store: PickerRecordStore | null | undefined): number | null {
    if (typeof workflowId !== 'string' || !workflowId || !store) return null;
    try {
        const record = findRecordByWorkflow(workflowId, readOnlyStorage(store));
        return record ? userFrameTimeOf(record, seek) : null;
    } catch {
        return null;
    }
}

export function pickerSidForWorkflow(workflowId: unknown, store: PickerRecordStore | null | undefined): string | null {
    if (typeof workflowId !== 'string' || workflowId.length === 0 || !store) return null;
    try {
        return findRecordByWorkflow(workflowId, readOnlyStorage(store))?.sid ?? null;
    } catch {
        return null;
    }
}

/**
 * R14-SH16: `readOnlyStorage` plus persistence's write, through `StorageService.store` (the same `<prefix>-<key>` join
 * `browserStorage()` uses), read back through `get` so a store that swallowed the write (the service's in-memory fallback,
 * quota) reports `false` and persistence says `disabled`. `remove` stays a no-op: the verdict never deletes a record.
 */
function verdictStorage(store: PickerRecordStore): PickerStorage {
    const base = readOnlyStorage(store);
    const head = `${FRAMEPICKER_STORAGE_PREFIX}-`;
    return {
        ...base,
        write(fullKey, value) {
            if (typeof store.store !== 'function' || !fullKey.startsWith(head)) return false;
            try {
                const expected = JSON.stringify(value);
                store.store(FRAMEPICKER_STORAGE_PREFIX, fullKey.slice(head.length), value);
                return JSON.stringify(base.read(fullKey)) === expected;
            } catch {
                return false;
            }
        },
    };
}

/** R14-SH16: what a `/downloads` card says about its job -- `done`, `failed`, or null while it is still in flight. */
export type PickerCardVerdict = 'done' | 'failed' | null;

/**
 * R14-SH16 (R14Q-56 ruling (a), 89cff12b2): the card's terminal verdict, from the same signals the card renders by.
 * FAILED: a failed upload (`status: 'failed'`), a result that says `error: true`, the socket's `processStageSubState:
 * 'failed'`, or a structured `compositionFailure`. DONE: the result landed (`output` / `outputFiles[]`) or the socket
 * reached `processStage: 'completed'`, and none of the failure signals. Anything else is still in flight (null). Pure.
 */
export function pickerCardVerdict(fileUpload: unknown): PickerCardVerdict {
    if (!fileUpload || typeof fileUpload !== 'object') return null;
    const fu = fileUpload as { status?: unknown; result?: Record<string, unknown> | null; ws?: Record<string, unknown> | null };
    const result = fu.result && typeof fu.result === 'object' ? fu.result : null;
    const ws = fu.ws && typeof fu.ws === 'object' ? fu.ws : null;
    const failed = fu.status === 'failed'
        || result?.error === true
        || ws?.processStageSubState === 'failed'
        || (!!result?.compositionFailure && typeof result.compositionFailure === 'object');
    if (failed) return 'failed';
    const landed = !!result && (
        (typeof result.output === 'string' && result.output !== '')
        || (Array.isArray(result.output) && result.output.length > 0)
        || (Array.isArray(result.outputFiles) && result.outputFiles.length > 0));
    if (landed || ws?.processStage === 'completed') return 'done';
    return null;
}

/**
 * R14-SH16: hand the verdict to the picker's record -- persistence's `recordJobFinish(sid, [workflowId], verdict, storage,
 * now)` on the record `findRecordByWorkflow` names (the same resolution as the badges). Returns the sid written to, or null
 * when nothing was: no verdict yet, no record names the workflow, a read-only store, storage disabled (persistence's own
 * `disabled` / `conflict` answers). Never throws. The CALLER keeps the once-per-verdict guard; this is one write.
 */
export function recordPickerJobFinish(workflowId: unknown, verdict: PickerCardVerdict, store: PickerRecordStore | null | undefined, now: () => number = Date.now): string | null {
    if (!verdict || typeof workflowId !== 'string' || !workflowId || !store) return null;
    try {
        const storage = verdictStorage(store);
        const sid = findRecordByWorkflow(workflowId, storage)?.sid ?? null;
        if (!sid) return null;
        return recordJobFinish(sid, [workflowId], verdict, storage, now).status === 'saved' ? sid : null;
    } catch {
        return null;
    }
}

/**
 * `/<slug>?tab=picker&psid=<sid>` (§6.1). Without a `sid` the link still opens the picker tab:
 * the picker owns the expired case ("This video isn't on our servers any more…" + the file
 * chooser), and a card that hid the link when storage was disabled would take the whole feature
 * away with it (D5).
 *
 * The sid is written only when `isValidPickerSid` accepts it — the same rule `openPickerTab`
 * applies — because `readPickerUrl` would read anything else as `null` anyway, and a URL that
 * visibly carries an id the picker then ignores is worse than one that carries none.
 */
export function pickAnotherFrameHref(slug: string | null | undefined, sid: string | null, jobId?: unknown): string | null {
    if (!slug) return null;
    const params = new URLSearchParams();
    params.set('tab', PICKER_TAB_ID);
    if (isValidPickerSid(sid)) params.set(PICKER_SID_PARAM, sid);
    // Review 7 JE-01: THIS card's job -- every card of a session links the same psid, and without it the picker restored the
    // session's latest state (a later ZIP's pins on a single-frame card). The picker restores this job's own state.
    if (isValidPickerSid(sid) && isValidPickerSid(jobId)) params.set(PICKER_JOB_PARAM, jobId as string);
    // IT2 (Y2, saas N2): the link names its source so the root counts FRAMEPICKER_OPENED{downloads_link}
    // exactly once; a bare `psid` reload is `url`.
    params.set(PICKER_PSRC_PARAM, DOWNLOADS_LINK_SOURCE);
    return `/${slug.replace(/^\/+/, '')}?${params.toString()}`;
}

/** The narrow shape `isFramePickerWorkflow` reads. Structural, so a spec needs no `FileUpload`. */
export interface FramePickerWorkflowFacts {
    workflowId?: unknown;
    /** T4: `FileUpload.id` -- the workflow's uuid, which replay-history is keyed by (`workflowId` is the Mongo id the record lists). */
    id?: unknown;
    options?: unknown;
    result?: { processingNotes?: unknown } | null;
    pageData?: { path?: string } | null;
    uniqueProcessType?: string;
    /** T4: the edge that ran the job (`processorMetadataList[0].processDomain`, else its `uploadDomain`) -- where the rehydrate asks. */
    endpointData?: { processorMetadataList?: Array<{ processDomain?: unknown; uploadDomain?: unknown } | null> | null } | null;
}

/**
 * Editor re-open T3 (EDITOR_REOPEN_PLAN.md §4, R-A): the picker context of a card, or null -- THE predicate, mirroring
 * `studio/export/reedit.ts#studioCompositionOf`. The landed processing note, else (while queued, Q16) the request's
 * frame-selection keys GATED on the picker having recorded this workflow (Quick convert writes the very same keys, so the
 * options alone never say "picker"). The card's `framePickerNote`, its editor button, its Re-edit destination and
 * `FILE_DOWNLOAD{source}` all read this one function, so none of them can disagree. Never throws.
 */
export function framePickerWorkflowNoteOf(fileUpload: FramePickerWorkflowFacts | null | undefined, store: PickerRecordStore | null | undefined): FramePickerNote | null {
    if (!fileUpload) return null;
    const fromNote = framePickerNoteOf(fileUpload.result?.processingNotes);
    if (fromNote) return fromNote;
    return framePickerNoteFromRequest(fileUpload.options, pickerSidForWorkflow(fileUpload.workflowId, store) !== null);
}

/** Is this card a Frame picker job? See `framePickerWorkflowNoteOf`. */
export function isFramePickerWorkflow(fileUpload: FramePickerWorkflowFacts | null | undefined, store: PickerRecordStore | null | undefined): boolean {
    return framePickerWorkflowNoteOf(fileUpload, store) !== null;
}

/** The tool page a card came from (`pageData.path`, else `uniqueProcessType`) -- the same rule Re-edit and the Studio use. */
export function cardSlugOf(fileUpload: FramePickerWorkflowFacts | null | undefined): string | null {
    const slug = fileUpload?.pageData?.path || fileUpload?.uniqueProcessType;
    return typeof slug === 'string' && slug !== '' ? slug : null;
}

/**
 * R-E's one action for an expired picker card: the picker tab on the SAME page with no `psid` and `pfresh=1` -- an EMPTY
 * picker session (`root/session.ts`), neither the expired copy a stale id would produce nor the page's latest record. No
 * `psrc`: this is not a re-open of the card's job.
 */
export function startNewPickerHref(slug: string | null | undefined): string | null {
    if (!slug) return null;
    // Wave B: `pfresh=1` -- an EMPTY picker session, never the page's latest record (`root/session.ts#resolveSession`).
    return `/${slug.replace(/^\/+/, '')}?tab=${encodeURIComponent(PICKER_TAB_ID)}&${PICKER_FRESH_PARAM}=1`;
}

/**
 * Editor re-open T3 (R-A, R-E): where a card's editor key goes.
 * - `studio`: a Studio card whose project this browser still holds -- `openStudioReedit` navigates.
 * - `picker`: a picker card whose `FRAMEPICKER-<sid>` record names the workflow -- `href` is `pickAnotherFrameHref(slug, sid)`.
 * - `picker` (T4): no record, but the card names its workflow and edge -- `rehydratePickerHref` (`pwf`/`pdom`).
 * - `expired`: a Studio or picker card with nothing to re-open -- the card says so and offers ONE action (never the modal).
 * - `modal`: everything else (Quick convert) -- the generic "Edit Conversion Settings" modal, unchanged.
 * Studio is asked first (the pre-existing branch). `studioOpen` is injected so the spec needs no Studio storage.
 */
export type ReeditRoute =
    /** `href` (review 7 S3-05): the Studio rebuilds a project this browser lost from the workflow record; absent -> the local project. */
    | { kind: 'studio'; href?: string }
    | { kind: 'picker'; href: string }
    | { kind: 'expired'; editor: 'studio' | 'picker' }
    | { kind: 'modal' };

export interface ReeditRouteDeps {
    isStudio(fileUpload: FramePickerWorkflowFacts): boolean;
    studioOpen(fileUpload: FramePickerWorkflowFacts): boolean;
    /** Review 7 S3-05: where the Studio rebuilds this card's project from its workflow, or null (files past their lifetime, no edge). */
    studioRehydrateHref?(fileUpload: FramePickerWorkflowFacts): string | null;
}

export function reeditRouteOf(fileUpload: FramePickerWorkflowFacts | null | undefined, store: PickerRecordStore | null | undefined, deps: ReeditRouteDeps): ReeditRoute {
    if (!fileUpload) return { kind: 'modal' };
    if (deps.isStudio(fileUpload)) {
        if (deps.studioOpen(fileUpload)) return { kind: 'studio' };
        const rebuild = deps.studioRehydrateHref?.(fileUpload) ?? null;
        return rebuild ? { kind: 'studio', href: rebuild } : { kind: 'expired', editor: 'studio' };
    }
    if (!isFramePickerWorkflow(fileUpload, store)) return { kind: 'modal' };
    const sid = pickerSidForWorkflow(fileUpload.workflowId, store);
    const href = sid ? pickAnotherFrameHref(cardSlugOf(fileUpload), sid, fileUpload.workflowId) : rehydratePickerHref(fileUpload);
    return href ? { kind: 'picker', href } : { kind: 'expired', editor: 'picker' };
}

/**
 * Editor re-open T4: a picker card this browser holds no record for -- the picker href that rebuilds the session from the
 * workflow record: `psid` = the card's `workflowId` (a valid sid with no record, so `resolveSession` looks further; the rebuilt
 * record lists it, so the next click finds the record by it), `pwf` = the workflow's uuid (`FileUpload.id`, what replay-history
 * is keyed by), `pdom` = the card's edge. Null (→ the expired state, as before) when the card lacks either id, an edge or a slug.
 */
export function rehydratePickerHref(fileUpload: FramePickerWorkflowFacts | null | undefined): string | null {
    const wf = fileUpload?.workflowId;
    const uuid = fileUpload?.id;
    if (!isValidPickerSid(wf) || !isValidPickerWorkflowId(uuid)) return null;
    const pm = fileUpload?.endpointData?.processorMetadataList?.[0];
    const domain = pickerProcessDomainOf(pm?.processDomain) ?? pickerProcessDomainOf(pm?.uploadDomain);
    const base = domain ? pickAnotherFrameHref(cardSlugOf(fileUpload), wf) : null;
    if (!base) return null;
    const params = new URLSearchParams();
    params.set(PICKER_WORKFLOW_PARAM, uuid);
    params.set(PICKER_DOMAIN_PARAM, domain as string);
    return `${base}&${params.toString()}`;
}

/**
 * R-E copy: one short sentence and one action per editor. `upload` (FIX-2, UX-SPEC §2.2 X2-1 / X2-2): a Quick convert
 * card whose UPLOAD is gone -- the same expired-state component, with "Upload it again" as its action.
 */
export const REEDIT_EXPIRED_COPY = {
    picker: { line: 'This video is no longer on our servers.', action: 'Pick frames from a new video' },
    studio: { line: "This project's files are no longer on our servers.", action: 'Make another video' },
    upload: { line: X2_COPY.expiredLine, action: X2_COPY.uploadAgain },
} as const;

/**
 * R14 batch 2 (ask 8, review BUG-026): what the picker's record knows about the clip a ONE-frame card came from,
 * for the badge's "Frame 45 of 240 · 0:01.467". `duration`/`fps` are the probe's answer persisted with the staged
 * file (`StagedFileRef`, written by `export/convert.ts#fileRefOf`). `vfr` is the probe's verdict on THAT ref, read
 * structurally (`isVfrRef`) because the field is not on `StagedFileRef` yet (R14 Sr review 2 item 6, slice L's row)
 * -- absent reads as "not known to be VFR". `fps` is null when the record has none (an older record, a probe that
 * said nothing): the badge then says the time only.
 */
export interface PickerClip {
    duration: number;
    fps: number | null;
    vfr: boolean;
    /**
     * R14 (R14Q-15): the probe's `frame_count` / `avg_framerate` as the ref persists them (`StagedFileRef.frameCount` /
     * `avgFps`, 324a8d6ad); null when the record has none. The badge's "of N" is `createTimeline(duration, fps, facts)` --
     * the counter's own call (`root/source.ts#timelineOf`) -- so 1188 counted beside 1190 computed reads 1188 on both.
     */
    frameCount: number | null;
    avgFps: number | null;
    /**
     * Review 7 JE-04: the edge's frame-rate verdict as the picker had it (`StagedFileRef.frameRateMode`). The picker's timeline
     * takes the probe's count over the container's length under 'CFR' (`createTimeline`, P1-06/P1-09: 901 frames in a 30.528 s
     * MOV, not 916), so the card's timeline must be built with it or its total drifts ("of 915"). Absent when unknown.
     */
    mode?: 'CFR' | 'VFR';
}

/**
 * R14 Sr review 2 item 6: is this stored ref a variable-frame-rate clip? Both spellings the picker side has named are
 * read -- `vfr: true` (this card's committed spelling) and `frameRateMode: 'VFR'` (slice L's plan for `StagedFileRef`,
 * the server's `frame_rate_mode` word) -- so the badge turns time-only the day either one reaches the record.
 * Anything else (absent, `'CFR'`, garbage) reads false. Never throws.
 */
export function isVfrRef(rawRef: unknown): boolean {
    if (!rawRef || typeof rawRef !== 'object') return false;
    const r = rawRef as Record<string, unknown>;
    return r.vfr === true || r.frameRateMode === 'VFR';
}

/**
 * The record naming `workflowId` -- persistence's parsed shape (`findRecordByWorkflow`'s rule) AND the raw stored
 * object beside it. The raw object is read for ONE thing the parser does not carry across yet: a ref's VFR verdict
 * (`isVfrRef`). Everything else (`thumbs`, `jobs`, `staged`, `file`) is read through `parseRecord`, so this card and
 * the picker accept a record by the same rule. Never throws; a broken record reads as none.
 */
function recordWithRawByWorkflow(workflowId: string, store: PickerRecordStore): { record: PickerRecord; raw: Record<string, unknown> } | null {
    const storage = readOnlyStorage(store);
    for (const key of storage.keys()) {
        if (!sidFromKey(key)) continue;
        const raw = storage.read(key);
        const record = parseRecord(raw);
        if (record && record.workflowIds.includes(workflowId)) return { record, raw: raw as Record<string, unknown> };
    }
    return null;
}

/**
 * R14 Sr review 2 item 5(b): the ref of the clip `workflowId`'s job was made FROM -- not `record.file`, the clip
 * SELECTED at the last save, which with two staged clips numbered job A's frame on clip B's rate. The record's
 * `jobs[]` (slice J) name each job's `fileUuid`; the ref carrying that uuid is looked up in `staged[]` (every staged
 * clip, pane order) and in `file`. A record with no job for this workflow (written before J) falls back to `file`, as
 * before. A job whose uuid no ref carries resolves to NONE: the badge then says the time only rather than a frame
 * number on another clip's rate. `rawRef` is the same ref as stored, for `isVfrRef`.
 */
export function clipRefForWorkflow(record: PickerRecord, raw: Record<string, unknown> | null | undefined, workflowId: string): { ref: StagedFileRef; rawRef: unknown } | null {
    const job = record.jobs?.find((j) => j.id === workflowId);
    const ref = job
        ? record.staged?.find((s) => s.uuid === job.fileUuid) ?? (record.file?.uuid === job.fileUuid ? record.file : null)
        : record.file;
    if (!ref) return null;
    const stagedRaw = raw?.staged;
    const candidates: unknown[] = [raw?.file, ...(Array.isArray(stagedRaw) ? stagedRaw : [])];
    const rawRef = candidates.find((r) => !!r && typeof r === 'object' && (r as Record<string, unknown>).uuid === ref.uuid);
    return { ref, rawRef };
}

/** The clip a workflow's job was made from, or null when no record names the workflow (or names no clip for it). Never throws. */
export function pickerClipForWorkflow(workflowId: unknown, store: PickerRecordStore | null | undefined): PickerClip | null {
    if (typeof workflowId !== 'string' || !workflowId || !store) return null;
    try {
        const found = recordWithRawByWorkflow(workflowId, store);
        if (!found) return null;
        const clip = clipRefForWorkflow(found.record, found.raw, workflowId);
        if (!clip) return null;
        const { duration, fps, frameCount, avgFps } = clip.ref;
        const positive = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) && x > 0 ? x : null);
        const rawMode = (clip.ref as { frameRateMode?: unknown }).frameRateMode ?? (clip.rawRef as { frameRateMode?: unknown } | null)?.frameRateMode;
        return {
            duration: positive(duration) ?? 0,
            fps: positive(fps),
            vfr: isVfrRef(clip.rawRef),
            frameCount: positive(frameCount),
            avgFps: positive(avgFps),
            mode: rawMode === 'CFR' || rawMode === 'VFR' ? rawMode : undefined,
        };
    } catch {
        return null;
    }
}

/** The server's delivered frame: 1-based `frame` and its start `at` (seconds) -- R14-SRV5's note / name grammar. */
export interface DeliveredFrame {
    frame: number;
    at: number;
}

/**
 * R14 (R14Q-14): the delivered frame off the saved NAME, `<stem>-f<n, %04d>-<m>_<ss>.<mmm>.<ext>` (shared-lib
 * `FramePickerNaming`, mirrored by the picker's `COPY.savesAs`). The card's `result.output` / `outputFiles[]` are the
 * edge's storage names, which only sometimes carry the user-visible one; the note is the first source, this the second.
 */
export function parseDeliveredFrameName(name: unknown): DeliveredFrame | null {
    if (typeof name !== 'string') return null;
    const m = /-f(\d+)-(\d+)_(\d{2})\.(\d{3})\.[A-Za-z0-9]+$/.exec(name.trim());
    if (!m) return null;
    const frame = Number(m[1]);
    if (!Number.isInteger(frame) || frame < 1) return null;
    return { frame, at: Number(m[2]) * 60 + Number(m[3]) + Number(m[4]) / 1000 };
}

/** The first output's name off a result (`output` as a string, else `outputFiles[0]`), for `parseDeliveredFrameName`. */
export function outputNameOf(result: unknown): string | null {
    if (!result || typeof result !== 'object') return null;
    const r = result as Record<string, unknown>;
    if (typeof r.output === 'string') return r.output;
    const files = r.outputFiles;
    return Array.isArray(files) && typeof files[0] === 'string' ? files[0] : null;
}

/**
 * The frame the server DELIVERED for a landed ONE-frame result: the note's `frame=` with `at=` (else its `t=`), else
 * the saved name's `f<n>-<clock>`. Null on a line without a frame (VFR, unknown rate, an older jar).
 */
export function deliveredFrameOf(landed: FramePickerNote | null | undefined, outputName: unknown): DeliveredFrame | null {
    if (!landed || landed.mode !== 'ONE') return null;
    if (landed.frame !== undefined) {
        const at = landed.at ?? landed.t;
        if (at !== null) return { frame: landed.frame, at };
    }
    return parseDeliveredFrameName(outputName);
}

/**
 * Everything the badge may know about a ONE-frame card's time, in the order it trusts them (R14Q-14 / 15 / 16):
 * - `delivered`: the server's frame and its start, once the result has landed on a CFR source -- the truth of the file.
 * - `resultT`: the server's `t=` on a landed result that names no frame (VFR / unknown rate / older jar): the file is
 *   `<stem>-<clock(t)>`, so the badge says that instant.
 * - `userT`: the frame time the USER chose, from the record's `frameTimes` (ms-rounded on write, `export/convert.ts#frameTimeMap`).
 * - `wireT`: the instant the request asked ffmpeg for (`options.screenshotTime`, else the note's `t=`).
 * Any of them null when not known.
 */
export interface FrameBadgeFacts {
    delivered: DeliveredFrame | null;
    resultT: number | null;
    userT: number | null;
    wireT: number | null;
}

/** `"1.55::SECONDS"` / `"1.55"` / `1.55` → seconds; anything else null (`persistence.seekKey`'s reading of the wire). */
export function seekSeconds(seek: number | string | null | undefined): number | null {
    const raw = typeof seek === 'string' ? seek.split('::')[0].trim() : seek;
    const n = typeof raw === 'number' ? raw : raw === '' || raw === undefined || raw === null ? NaN : Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * SMALL-1 (prod fixes 2026-10-02): did the server deliver the clip's LAST frame for a request past it? Read off the structured
 * notes (`result.noteCodes`, PLAN.md C1) -- `FRAME_CLAMPED_TO_END` -- never off the note's human text. False for anything else.
 */
export function clampedToEnd(result: unknown): boolean {
    if (!result || typeof result !== 'object') return false;
    const codes = (result as Record<string, unknown>).noteCodes;
    return Array.isArray(codes) && codes.some((c) => !!c && typeof c === 'object' && (c as Record<string, unknown>).code === 'FRAME_CLAMPED_TO_END');
}

/**
 * The card's facts for the badge: the landed note (or null while queued), the result, the seek and the user's time. SMALL-1: when the
 * server clamped the pick to the clip's last frame the user's own time names a frame that was NOT delivered, so it is dropped and the
 * server's `t=` (the delivered frame's time) numbers the badge.
 */
export function frameBadgeFacts(landed: FramePickerNote | null | undefined, result: unknown, seek: number | string | null | undefined, userT: number | null): FrameBadgeFacts {
    const one = landed && landed.mode === 'ONE' ? landed : null;
    return {
        delivered: deliveredFrameOf(one, outputNameOf(result)),
        resultT: one?.t ?? null,
        userT: one && clampedToEnd(result) ? null : userT,
        wireT: seekSeconds(seek),
    };
}

/**
 * The clip's grid, built EXACTLY as the picker's counter builds it (`root/source.ts#timelineOf` → `createTimeline(duration,
 * fps, probeFactsOf(info))`): under CFR the probe's count replaces `round(duration × fps)` when within 2 frames (1188 / 865,
 * R14Q-15), under VFR the timeline is `variable`. Null when the rate or length is unknown, or the grid is empty.
 */
export function clipTimeline(clip: PickerClip | null | undefined): Timeline | null {
    if (!clip || clip.fps === null || clip.duration <= 0) return null;
    // Review 7 JE-04: the SAME facts the picker built its timeline from, the CFR verdict included (see `PickerClip.mode`).
    const tl = createTimeline(clip.duration, clip.fps, { frameCount: clip.frameCount, avgFps: clip.avgFps, mode: clip.vfr ? 'VFR' : (clip.mode ?? null) });
    return tl.frameCount > 0 ? tl : null;
}

/**
 * R14 (R14Q-14): the 0-based frame the SERVER extracts for a wire instant -- ironman `FramePickerRequest.frameIndexAt`,
 * `ceil(t·fps − 1e-3)`: `-ss t` delivers the first frame with pts ≥ t, and the picker writes a quarter frame early
 * (`frameSeekTime`), so the ceiling names the frame the user saw. Clamped into the grid. Used only when the record does not
 * carry the user's own time; never for a typed time (that is the containing rule below).
 */
export function frameIndexFromWire(wireT: number, tl: Timeline): number {
    return frameFromWire(wireT, tl); // WP-02: the one wire -> frame rule (`media/timeline.ts`)
}

/**
 * R14 (R14Q-14): the 0-based frame the USER's time names. `frameTimes` holds the picked time rounded to the millisecond,
 * and a frame whose start rounds DOWN (43/30 = 1.43333 → 1.433) is 0.33 ms inside the previous frame for a bare floor
 * (1.433 × 30 = 42.99 → 42, the "Frame 43" QA saw for the file named `f0044`). The transport's own rule for a typed,
 * ms-precision time is `frameIndex(t + TYPED_TIME_SLACK_S)` (`media/timeline.ts`); the card applies the same one.
 */
export function frameIndexFromUserTime(userT: number, tl: Timeline): number {
    return frameIndex(userT + TYPED_TIME_SLACK_S, tl);
}

function griddedLabel(k: number, tl: Timeline): string {
    return `Frame ${k + 1} of ${tl.frameCount} · ${formatHms(frameStart(k, tl))}`;
}

/**
 * The badge's words (R14Q-14 / 15 / 16):
 * - Result landed with the server's frame: "Frame 44 of 240 · 0:01.433" -- the number and the time are the SERVER's, the
 *   count the clip's grid; without a grid (record swept, or a VFR verdict the server overrode) "Frame 44 · 0:01.433".
 * - Result landed without a frame (`t=` only): "Frame at 0:10.000" -- the server's instant, which names the file. A CFR
 *   clip whose line is an older jar's is still numbered here, off the user's time (containing rule + typing slack) or the
 *   wire (the server's rule); its time is the frame's START, as the server's `at=` would be.
 * - Queued, CFR grid: the same numbering off the user's time, else the wire -- so the badge does not change when the
 *   result lands (spec: every k at 24 / 25 / 30 / 29.97 / 50 / 59.94 / 60 fps).
 * - Queued, VFR: "Frame at <wire>" -- the instant the request carries, never the playhead (R14Q-16: 0:10.000, not 0:10.020).
 * - No clip / rate unknown: "Frame at <user's time, else the wire>", as before.
 * Null when there is no time at all to say.
 */
export function frameBadgeText(facts: FrameBadgeFacts, clip: PickerClip | null | undefined): string | null {
    const tl = clipTimeline(clip);
    const grid = tl && !tl.variable ? tl : null;
    if (facts.delivered) {
        const { frame, at } = facts.delivered;
        return grid ? `Frame ${frame} of ${grid.frameCount} · ${formatHms(at)}` : `Frame ${frame} · ${formatHms(at)}`;
    }
    if (facts.resultT !== null) {
        if (!grid) return `Frame at ${formatHms(facts.resultT)}`;
        const k = facts.userT !== null ? frameIndexFromUserTime(facts.userT, grid) : frameIndexFromWire(facts.resultT, grid);
        return griddedLabel(k, grid);
    }
    if (!grid) {
        const t = clip?.vfr ? facts.wireT ?? facts.userT : facts.userT ?? facts.wireT;
        return t === null ? null : `Frame at ${formatHms(t)}`;
    }
    if (facts.userT !== null) return griddedLabel(frameIndexFromUserTime(facts.userT, grid), grid);
    if (facts.wireT !== null) return griddedLabel(frameIndexFromWire(facts.wireT, grid), grid);
    return null;
}

/** Ask 8's one call for the card: the badge text for `facts` on the clip `workflowId`'s record describes. */
export function pickerFrameLabelForWorkflow(workflowId: unknown, facts: FrameBadgeFacts, store: PickerRecordStore | null | undefined): string | null {
    return frameBadgeText(facts, pickerClipForWorkflow(workflowId, store));
}

/**
 * R14 (R14U-18): what the picker's RECORD says a many-frame job is -- the first source for the card's lead line. `mode` is
 * the JOB's (`jobs[].mode`, slice J), else the record's; `range` is the record's own `{start, end}` when the user set one
 * (the ms-exact numbers the panel showed, not the wire's quarter-frame-early start), `end: null` (open-ended) closed on the
 * job's clip length when the record knows it; `count` is the picker's OWN rule (`picker-state.ts#frameCount` over the
 * restored state and the clip's length / rate -- the number the header, the summary chip and the grid showed), so the
 * card says the count the user pressed for. Null when no record names the workflow, or the job is ONE frame.
 */
export interface PickerJobRange {
    mode: 'SEQUENCE' | 'SHEET';
    range: { start: number; end: number } | null;
    count: number | null;
}

export function pickerJobRangeForWorkflow(workflowId: unknown, store: PickerRecordStore | null | undefined): PickerJobRange | null {
    if (typeof workflowId !== 'string' || !workflowId || !store) return null;
    try {
        const found = recordWithRawByWorkflow(workflowId, store);
        if (!found) return null;
        const { record } = found;
        const job = record.jobs?.find((j) => j.id === workflowId);
        const mode = job?.mode ?? record.mode;
        if (mode === 'ONE') return null;
        const clip = clipRefForWorkflow(record, found.raw, workflowId);
        const duration = typeof clip?.ref.duration === 'number' && Number.isFinite(clip.ref.duration) && clip.ref.duration > 0 ? clip.ref.duration : null;
        const fps = typeof clip?.ref.fps === 'number' && Number.isFinite(clip.ref.fps) && clip.ref.fps > 0 ? clip.ref.fps : null;
        let range: PickerJobRange['range'] = null;
        if (record.range) {
            const end = record.range.end ?? duration;
            if (end !== null && end >= record.range.start) range = { start: record.range.start, end };
        }
        let count: number | null = null;
        if (duration !== null) {
            // The picker's own count rule wants its state shape; `fromRecord` is persistence's restore of exactly that.
            const n = pickerFrameCount({ ...fromRecord(record), mode } as PickerState, duration, fps);
            if (Number.isInteger(n) && n >= 1) count = n;
        }
        if (count === null && record.spacing === 'BY_COUNT' && Number.isInteger(record.count) && record.count >= 1) count = record.count;
        return { mode, range, count };
    } catch {
        return null;
    }
}

/** R14U-18: the lead word of a sheet card is the picker's own label for the mode ("Contact sheet"); a sequence leads "Frames". */
export const RANGE_BADGE_SHEET_WORD = FRAME_PICKER_COPY.modeLabel('CONTACT_SHEET') ?? 'Contact sheet';
export const RANGE_BADGE_SEQUENCE_WORD = 'Frames';
/** R14U-25: the phone's lead word for a sheet -- the picker's own short word for the mode (its Video / Sheet toggle); "Frames" is already short. */
export const RANGE_BADGE_SHEET_WORD_SHORT = FRAME_PICKER_COPY.sheetToggleSheet ?? 'Sheet';

/** How many outputs a landed result carries (`outputFiles[]`, else an `output` array), the third source for a sequence's count. */
export function outputCountOf(result: unknown): number | null {
    if (!result || typeof result !== 'object') return null;
    const r = result as Record<string, unknown>;
    const files = Array.isArray(r.outputFiles) ? r.outputFiles : Array.isArray(r.output) ? r.output : null;
    return files && files.length > 0 ? files.length : null;
}

/**
 * R14U-18: the lead line of a many-frame card -- "Contact sheet · 12 frames · 0:02.000 – 0:04.000", "Frames · 10 · 0:02.000 –
 * 0:04.000" -- so the card says WHAT IT IS before anything else (the sheet card used to open with the labels apology and
 * carry no badge at all). Sources, in the order trusted: the picker's record (`PickerJobRange`: the user's own range and the
 * count the panel showed), then the server's landed line (`start=`/`end=`/`frames=`, R14-SRV7), then for a sequence the
 * result's own output count. Each part is said only when known: a swept record and an older jar's bare line read
 * "Contact sheet" alone. Null for a ONE-frame card or an ordinary conversion (the time badge owns ONE).
 */
export function rangeBadgeText(note: FramePickerNote | null | undefined, job: PickerJobRange | null | undefined, result: unknown): string | null {
    return rangeBadgeParts(note, job, result)?.text ?? null;
}

/**
 * R14U-25 (picker UX pass 4, P3): the lead line in parts. On a 390 px phone the sheet's full line sat beside a two-line
 * title and ran off the card, so the card swaps the LEAD WORD for the picker's short one in CSS ("Sheet") while the DOM
 * text, `aria-label` and `title` keep the full line: `text === lead + rest` ("Contact sheet" + " · 10 frames · 0:00.000 –
 * 0:04.500"); `rest` is '' when nothing but the mode is known. The count and the times are printed in full at every
 * width -- the clipped "0:04.5" of R14U-25 was a cut, not a format.
 */
export interface RangeBadgeParts {
    /** The whole line, `lead + rest` -- what `rangeBadgeText` says. */
    text: string;
    /** "Contact sheet" / "Frames". */
    lead: string;
    /** The phone's word for `lead`: "Sheet" / "Frames". */
    shortLead: string;
    /** " · 10 frames · 0:00.000 – 0:04.500", or ''. */
    rest: string;
}

/** Editor re-open T5: the lead words of a Picked-frames card (the picker's own label, less the count) and the phone's short one. */
export const RANGE_BADGE_PICKED_WORD = 'Picked frames';
export const RANGE_BADGE_PICKED_WORD_SHORT = 'Picked';
/** T5: how many frames the badge names before it says "+N more". */
export const PICKED_BADGE_NAMED = 3;

/**
 * Editor re-open T5: the lead line of a Picked-frames card -- "Picked frames · 2 · 44 (0:01.433), 47 (0:01.533)" once the edge's line
 * named the frames (CFR), "Picked frames · 2 · 0:01.450, 0:02.000" by time otherwise (and while queued, off the request), "+N more" past
 * `PICKED_BADGE_NAMED`. The count is the line's `frames=`, else the result's output count, else the picks.
 */
export function pickedBadgeParts(note: FramePickerNote, result: unknown): RangeBadgeParts {
    const picks = note.picks ?? [];
    const count = note.frames ?? outputCountOf(result) ?? (picks.length || null);
    const tail: string[] = [];
    if (count !== null) tail.push(String(count));
    if (picks.length) {
        const named = picks.slice(0, PICKED_BADGE_NAMED).map((p) => (p.frame !== undefined ? `${p.frame} (${formatHms(p.at)})` : formatHms(p.at)));
        const more = picks.length - named.length;
        tail.push(named.join(', ') + (more > 0 ? ` +${more} more` : ''));
    }
    const rest = tail.length ? ` · ${tail.join(' · ')}` : '';
    return { text: RANGE_BADGE_PICKED_WORD + rest, lead: RANGE_BADGE_PICKED_WORD, shortLead: RANGE_BADGE_PICKED_WORD_SHORT, rest };
}

export function rangeBadgeParts(note: FramePickerNote | null | undefined, job: PickerJobRange | null | undefined, result: unknown): RangeBadgeParts | null {
    if (note?.mode === 'PICKED') return pickedBadgeParts(note, result);
    const mode = job?.mode ?? (note && note.mode !== 'ONE' ? note.mode : null);
    if (!mode) return null;
    const range = job?.range ?? (note && note.start !== undefined && note.end !== undefined ? { start: note.start, end: note.end } : null);
    const count = job?.count ?? note?.frames ?? (mode === 'SEQUENCE' ? outputCountOf(result) : null);
    const tail: string[] = [];
    if (count !== null) tail.push(mode === 'SHEET' ? `${count} ${count === 1 ? 'frame' : 'frames'}` : String(count));
    if (range) tail.push(`${formatHms(range.start)} – ${formatHms(range.end)}`);
    const lead = mode === 'SHEET' ? RANGE_BADGE_SHEET_WORD : RANGE_BADGE_SEQUENCE_WORD;
    const shortLead = mode === 'SHEET' ? RANGE_BADGE_SHEET_WORD_SHORT : RANGE_BADGE_SEQUENCE_WORD;
    const rest = tail.length ? ` · ${tail.join(' · ')}` : '';
    return { text: lead + rest, lead, shortLead, rest };
}

/** R14U-18's one call for the card: the lead line for `note` (landed, or the request stand-in) on the record `workflowId` names. */
export function pickerRangeLabelForWorkflow(workflowId: unknown, note: FramePickerNote | null | undefined, result: unknown, store: PickerRecordStore | null | undefined): string | null {
    return rangeBadgeText(note, pickerJobRangeForWorkflow(workflowId, store), result);
}

/** R14U-25: the same line as `pickerRangeLabelForWorkflow`, in parts, for the card's phone word swap. */
export function pickerRangeBadgeForWorkflow(workflowId: unknown, note: FramePickerNote | null | undefined, result: unknown, store: PickerRecordStore | null | undefined): RangeBadgeParts | null {
    return rangeBadgeParts(userTimesForPickedStandIn(workflowId, note, result, store), pickerJobRangeForWorkflow(workflowId, store), result);
}

/**
 * Review 7 JE-04: before the edge's note lands, a pinned-frames card reads the REQUEST, whose `screenshotTimes` are the seek
 * instants ffmpeg is asked for (a pin at 0:02.000 goes out as 1.983 on a 30 fps clip) -- the card listed "0:01.983". The
 * picker's record keeps the user's time under each seek (`frameTimes`, written by `export/convert.ts` for pins), so the
 * stand-in's times are mapped back through it. A landed note (the server's own frames and times) is left as written.
 */
function userTimesForPickedStandIn(workflowId: unknown, note: FramePickerNote | null | undefined, result: unknown, store: PickerRecordStore | null | undefined): FramePickerNote | null | undefined {
    if (note?.mode !== 'PICKED' || !note.picks?.length) return note;
    if (framePickerNoteOf((result as { processingNotes?: unknown } | null | undefined)?.processingNotes)) return note;
    const picks = note.picks.map((p) => {
        if (p.frame !== undefined) return p;
        const user = pickerFrameTimeForWorkflow(workflowId, p.at, store);
        return user === null ? p : { ...p, at: user };
    });
    return { ...note, picks };
}

/**
 * R14 batch 2 (ask 9 ← ask 10): the picture of the frame the user pressed Convert for, from the record's `thumbs`
 * (keyed like `frameTimes`, by the SEEK instant the request carries -- `options.screenshotTime` or the note's `t=`).
 * Read through persistence's own `thumbFor` on the PARSED record (R14 Sr review 2 item 5a): the prefix, the base64
 * body AND the size cap (`isThumbDataUrl`, `MAX_THUMB_CHARS`) are the picker's one rule, the same on the write side --
 * this module keeps no copy of it. A record without `thumbs`, or a value the picker would not have written, reads as
 * none. Never throws.
 */
export function pickerThumbForWorkflow(workflowId: unknown, seek: number | string | null | undefined, store: PickerRecordStore | null | undefined): string | null {
    if (typeof workflowId !== 'string' || !workflowId || !store) return null;
    try {
        const record = findRecordByWorkflow(workflowId, readOnlyStorage(store));
        // R14 Sr review 2 (H's per-file thumbs): two clips pressed at the same seek keep separate pictures, so the
        // card names the JOB's file; a record whose job carries no uuid falls back to the newest at that seek.
        const fileUuid = (record as { jobs?: Array<{ id?: unknown; fileUuid?: unknown }> } | null)?.jobs
            ?.find((j) => j?.id === workflowId)?.fileUuid;
        return thumbFor(record, seek, typeof fileUuid === 'string' ? fileUuid : undefined);
    } catch {
        return null;
    }
}

/**
 * Ask 9's source order for the card's thumbnail. While the result has not landed there is no edge picture, so the
 * pressed frame (`record`) stands in; once it has, the edge's poster of the DELIVERED file is the truth and takes
 * over (`fallback` keeps the pressed frame for the `onerror` path); nothing to show reads null.
 */
export function pickerThumbSource(edgePoster: string | null | undefined, recordThumb: string | null | undefined): { src: string; kind: 'edge' | 'record'; fallback: string | null } | null {
    if (edgePoster) return { src: edgePoster, kind: 'edge', fallback: recordThumb || null };
    if (recordThumb) return { src: recordThumb, kind: 'record', fallback: null };
    return null;
}

/**
 * Review 7 P2-07: shared-lib `FramePickerNaming.formatClock` -- seconds as the picker's counter reads them, file-name safe:
 * `<minutes>_<ss>.<mmm>` (`1.4667` → `0_01.467`, `65.5` → `1_05.500`), rounded to the millisecond, negative reads as zero.
 */
export function formatPickerClock(t: number): string {
    const ms = Math.round(Math.max(0, Number(t) || 0) * 1000);
    const minutes = Math.floor(ms / 60_000);
    const rest = ms % 60_000;
    return `${minutes}_${String(Math.floor(rest / 1000)).padStart(2, '0')}.${String(rest % 1000).padStart(3, '0')}`;
}

const pad4 = (n: number) => String(n).padStart(4, '0');

/**
 * Review 7 P2-07: the name a picker output SAVES as -- the client mirror of shared-lib `FramePickerNaming.readableName`, which
 * the edge's `Content-Disposition` and the ZIP entries both use (review 7 K3 / X1-04). The "Show Downloads" list labelled
 * frames "<stem> 0.jpg" while they saved as "<stem>-frame-0001.jpg"; a pinned-frames job hid which frame was which.
 * `index` is the 0-based `-z-<i>-` of the on-disk name, `<stem>` the source's base name. Null without a picker note.
 * Keep in step with FramePickerNaming.kt's table (the spec walks it row by row).
 */
export function pickerOutputFileName(note: FramePickerNote | null | undefined, sourceName: string, outputName: string): string | null {
    if (!note) return null;
    const base = String(outputName ?? '').split('\\').pop() || '';
    const dot = base.lastIndexOf('.');
    const ext = dot >= 0 ? base.slice(dot + 1) : '';
    const src = String(sourceName ?? '');
    const srcDot = src.lastIndexOf('.');
    const stem = srcDot > 0 ? src.slice(0, srcDot) : src;
    if (ext.toLowerCase() === 'zip') return `${stem}-frames.${ext}`;
    const z = /-z-(\d+)-y-/.exec(base);
    const index = z ? Number(z[1]) : 0;
    switch (note.mode) {
        case 'ONE':
            return typeof note.frame === 'number'
                ? `${stem}-f${pad4(note.frame)}-${formatPickerClock(note.at ?? note.t ?? 0)}.${ext}`
                : `${stem}-${formatPickerClock(note.t ?? 0)}.${ext}`;
        case 'SEQUENCE':
            return `${stem}-frame-${pad4(index + 1)}.${ext}`;
        case 'PICKED': {
            const pick = note.picks?.[index];
            if (!pick) return `${stem}-frame-${pad4(index + 1)}.${ext}`;
            return typeof pick.frame === 'number'
                ? `${stem}-f${pad4(pick.frame)}-${formatPickerClock(pick.at)}.${ext}`
                : `${stem}-${formatPickerClock(pick.at)}.${ext}`;
        }
        case 'SHEET':
            return note.start !== undefined && note.end !== undefined
                ? `${stem}-sheet-${formatPickerClock(note.start)}-${formatPickerClock(note.end)}.${ext}`
                : `${stem}-sheet.${ext}`;
        default:
            return null;
    }
}
